import * as React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Button,
  Divider,
  Dropdown,
  DropdownItem,
  DropdownList,
  Masthead,
  MastheadBrand,
  MastheadContent,
  MastheadLogo,
  MastheadMain,
  MastheadToggle,
  MenuToggle,
  Nav,
  NavExpandable,
  NavItem,
  NavList,
  Page,
  PageSidebar,
  PageSidebarBody,
  SkipToContent,
  Toolbar,
  ToolbarContent,
  ToolbarGroup,
  ToolbarItem,
} from '@patternfly/react-core';
import { IAppRoute, IAppRouteGroup, routes } from '@app/routes';
import { BarsIcon, PlusCircleIcon, UserIcon } from '@patternfly/react-icons';
import { useKeycloak } from '../components/KeycloakProvider';
import { TimerPill } from '../components/TimerPill';
import { CreateIssueModal } from '../components/CreateIssueModal';
import './AppLayout.css';

interface IAppLayout {
  children: React.ReactNode;
}

const AppLayout: React.FunctionComponent<IAppLayout> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = React.useState(true);
  const [isUserMenuOpen, setIsUserMenuOpen] = React.useState(false);
  const [isCreateIssueModalOpen, setIsCreateIssueModalOpen] = React.useState(false);
  const { keycloak } = useKeycloak();

  const userName = keycloak?.tokenParsed?.preferred_username || keycloak?.tokenParsed?.name || 'User';

  const handleLogout = () => {
    keycloak?.logout();
  };

  const userMenuItems = (
    <DropdownList>
      <DropdownItem key="user" isDisabled>
        Logged in as <strong>{userName}</strong>
      </DropdownItem>
      <Divider key="divider" />
      <DropdownItem key="logout" onClick={handleLogout}>
        Log out
      </DropdownItem>
    </DropdownList>
  );

  const headerToolbar = (
    <Toolbar id="masthead-toolbar" isFullHeight isStatic>
      <ToolbarContent>
        <ToolbarGroup align={{ default: 'alignCenter' }}>
          <ToolbarItem>
            <TimerPill />
          </ToolbarItem>
        </ToolbarGroup>
        <ToolbarGroup align={{ default: 'alignEnd' }}>
          <ToolbarItem>
            <Button
              variant="primary"
              icon={<PlusCircleIcon />}
              onClick={() => setIsCreateIssueModalOpen(true)}
              aria-label="Quick log"
            >
              Quick log
            </Button>
          </ToolbarItem>
          <ToolbarItem>
            <Dropdown
              isOpen={isUserMenuOpen}
              onSelect={() => setIsUserMenuOpen(false)}
              onOpenChange={setIsUserMenuOpen}
              popperProps={{ position: 'end' }}
              toggle={(toggleRef) => (
                <MenuToggle
                  ref={toggleRef}
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  isExpanded={isUserMenuOpen}
                  icon={<UserIcon />}
                >
                  {userName}
                </MenuToggle>
              )}
            >
              {userMenuItems}
            </Dropdown>
          </ToolbarItem>
        </ToolbarGroup>
      </ToolbarContent>
    </Toolbar>
  );

  const masthead = (
    <Masthead>
      <MastheadMain>
        <MastheadToggle>
          <Button
            icon={<BarsIcon />}
            variant="plain"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label="Toggle navigation"
          />
        </MastheadToggle>
        <MastheadBrand data-codemods>
          <MastheadLogo data-codemods>
            <NavLink to="/" className="app-logo">
              <span className="app-logo-text">Tracker</span>
            </NavLink>
          </MastheadLogo>
        </MastheadBrand>
      </MastheadMain>
      <MastheadContent>{headerToolbar}</MastheadContent>
    </Masthead>
  );

  const location = useLocation();

  const renderNavItem = (route: IAppRoute, index: number) => {
    const IconComponent = route.icon;
    return (
      <NavItem key={`${route.label}-${index}`} id={`${route.label}-${index}`} isActive={route.path === location.pathname}>
        <NavLink to={route.path}>
          {IconComponent && <span className="nav-item-icon"><IconComponent /></span>}
          {route.label}
        </NavLink>
      </NavItem>
    );
  };

  const renderNavGroup = (group: IAppRouteGroup, groupIndex: number) => (
    <NavExpandable
      key={`${group.label}-${groupIndex}`}
      id={`${group.label}-${groupIndex}`}
      title={group.label}
      isActive={group.routes.some((route) => route.path === location.pathname)}
    >
      {group.routes.map((route, idx) => route.label && renderNavItem(route, idx))}
    </NavExpandable>
  );

  const Navigation = (
    <Nav id="nav-primary-simple">
      <NavList id="nav-list-simple">
        {routes.map(
          (route, idx) => route.label && (!route.routes ? renderNavItem(route, idx) : renderNavGroup(route, idx)),
        )}
      </NavList>
    </Nav>
  );

  const Sidebar = (
    <PageSidebar>
      <PageSidebarBody>{Navigation}</PageSidebarBody>
    </PageSidebar>
  );

  const pageId = 'primary-app-container';

  const PageSkipToContent = (
    <SkipToContent
      onClick={(event) => {
        event.preventDefault();
        const primaryContentContainer = document.getElementById(pageId);
        primaryContentContainer?.focus();
      }}
      href={`#${pageId}`}
    >
      Skip to Content
    </SkipToContent>
  );
  return (
    <Page
      mainContainerId={pageId}
      masthead={masthead}
      sidebar={sidebarOpen && Sidebar}
      skipToContent={PageSkipToContent}
    >
      {children}
      <CreateIssueModal
        isOpen={isCreateIssueModalOpen}
        onClose={() => setIsCreateIssueModalOpen(false)}
      />
    </Page>
  );
};

export { AppLayout };
